import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { DataTable } from "@/components/DataTable";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollText, X } from "lucide-react";

const ENTITY_LABELS = {
  request: "Richiesta carburante", rapportino: "Rapportino", checkin: "Check-in",
  user: "Utente", vehicle: "Mezzo", fuel_card: "Carta carburante", commessa: "Commessa",
  listino: "Listino", unit: "Unità di misura", hour_type: "Tipo ore",
  equipment: "Attrezzatura", activity_type: "Tipo attività",
};
const ACTION_LABELS = {
  create: "Creazione", update: "Modifica", delete: "Eliminazione", approve: "Approvazione",
  reject: "Rifiuto", submit: "Trasmissione", request_edit: "Richiesta modifica",
};
const ACTION_STYLE = {
  create: "border-emerald-200 bg-emerald-50 text-emerald-700",
  update: "border-blue-200 bg-blue-50 text-[#002FA7]",
  delete: "border-rose-200 bg-rose-50 text-rose-700",
  approve: "border-emerald-200 bg-emerald-50 text-emerald-700",
  reject: "border-rose-200 bg-rose-50 text-rose-700",
  submit: "border-violet-200 bg-violet-50 text-violet-700",
  request_edit: "border-amber-200 bg-amber-50 text-amber-700",
};
const fmtDateTime = (s) =>
  s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }) : "-";

export default function Logs() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("tutte");
  const [action, setAction] = useState("tutte");
  const [userId, setUserId] = useState("tutti");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = () => {
    setLoading(true);
    const params = {};
    if (entity !== "tutte") params.entity = entity;
    if (action !== "tutte") params.action = action;
    if (userId !== "tutti") params.user_id = userId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/logs", { params }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { api.get("/logs/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  useEffect(load, [entity, action, userId, dateFrom, dateTo]);

  const hasFilters = entity !== "tutte" || action !== "tutte" || userId !== "tutti" || dateFrom || dateTo;
  const reset = () => { setEntity("tutte"); setAction("tutte"); setUserId("tutti"); setDateFrom(""); setDateTo(""); };

  const columns = [
    { key: "created_at", header: "Data e ora", sortable: true, sortValue: (r) => new Date(r.created_at).getTime(), className: "whitespace-nowrap text-slate-500", cell: (r) => fmtDateTime(r.created_at) },
    { key: "user_name", header: "Utente", sortable: true, className: "font-medium text-slate-900", cell: (r) => r.user_name || "—" },
    { key: "action", header: "Azione", sortable: true, cell: (r) => (
        <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${ACTION_STYLE[r.action] || "border-slate-200 bg-slate-50 text-slate-600"}`}>
          {ACTION_LABELS[r.action] || r.action}
        </span>
      ) },
    { key: "entity", header: "Tipo", sortable: true, className: "text-slate-600", cell: (r) => ENTITY_LABELS[r.entity] || r.entity },
    { key: "summary", header: "Dettaglio", className: "text-slate-600", cell: (r) => r.summary || "—" },
  ];

  return (
    <div>
      <div className="mb-4">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Log attività</h1>
        <p className="mt-1 text-sm text-slate-500">Registro di tutte le attività (creazione, modifica, eliminazione) di tutti gli utenti.</p>
      </div>

      <Card className="mb-4 rounded-lg border-slate-200 p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs text-slate-500">Dal</Label>
            <Input type="date" data-testid="logs-date-from" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} className="mt-1.5 w-40" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Al</Label>
            <Input type="date" data-testid="logs-date-to" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} className="mt-1.5 w-40" />
          </div>
          <div>
            <Label className="block text-xs text-slate-500">Utente</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid="logs-user-select" className="mt-1.5 w-52"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti gli utenti</SelectItem>
                {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.user_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="block text-xs text-slate-500">Tipo</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger data-testid="logs-entity-select" className="mt-1.5 w-52"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutte">Tutti i tipi</SelectItem>
                {Object.entries(ENTITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="block text-xs text-slate-500">Azione</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="logs-action-select" className="mt-1.5 w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutte">Tutte le azioni</SelectItem>
                {Object.entries(ACTION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {hasFilters && (
            <Button variant="outline" onClick={reset} data-testid="logs-reset" className="h-10 rounded-md"><X className="mr-2 h-4 w-4" />Azzera</Button>
          )}
        </div>
      </Card>

      <Card className="rounded-lg border-slate-200 shadow-sm">
        {loading ? (
          <p className="p-6 text-slate-400">Caricamento...</p>
        ) : (
          <div className="p-4">
            <DataTable
              data={items}
              testid="logs-table"
              rowTestId={(r) => `log-row-${r.id}`}
              searchKeys={["user_name", "summary", "entity", "action"]}
              searchPlaceholder="Cerca nel log..."
              initialSort={{ key: "created_at", dir: "desc" }}
              emptyIcon={ScrollText}
              emptyText="Nessuna attività registrata"
              columns={columns}
            />
          </div>
        )}
      </Card>
    </div>
  );
}
